import React, { useState, ChangeEvent, KeyboardEvent, useEffect } from 'react';
import {
  TextField, Chip, Paper, Button, FormControl, RadioGroup, FormControlLabel,
  Radio, Container, IconButton, Checkbox, InputAdornment, Tooltip, Typography, Box,
  FormGroup, MenuItem,
  Switch
} from '@mui/material';
import { MeetingMinute, ReportRequestParameters } from '../meetingMinutesSlice';
import { Localized, useLocalization } from '@fluent/react';
import { validateEmail } from '../../../../app/Utils';
import { AppTextFieldHandler } from '../../../../app/appTypes';
import AddIcon from '@mui/icons-material/Add';
import CloseRounded from '@mui/icons-material/CloseRounded';
import { SimpleMeetingMinutesReportSelect } from './SimpleMeetingMinutesReportSelect';
import { CustomMMReportOptions } from './CustomMMReportOptions';
import { ProjectProcessHeader } from '../../projectProcess/ProjectProcessApiClient';
import { set } from 'date-fns';

interface EmailInputProps {
  meetingMinute: MeetingMinute;
  processes: ProjectProcessHeader[];
  defaultAttachNoteAttachments: boolean;
  onListOfRecipientsChange: (listOfRecipients: string[]) => void;
  onAttachNoteAttachmentsChanged: (attach: boolean) => void;
  onReportRequestParametersChange: (dto: ReportRequestParameters) => void;
  operationalDashboardReport?: boolean;
}

const EmailInput: React.FC<EmailInputProps> = ({
  meetingMinute,
  processes,
  defaultAttachNoteAttachments,
  onListOfRecipientsChange,
  onAttachNoteAttachmentsChanged,
  onReportRequestParametersChange,
  operationalDashboardReport = false
}) => {
  const { l10n } = useLocalization();

  const lastMeeting = meetingMinute.meetings.find(meeting => meeting.id === meetingMinute.lastMeetingId);
  const lastMeetingParticipants = lastMeeting
    ? lastMeeting.attendanceList.map(a => a.email).filter(email => email.trim() !== '')
    : [];
  const allParticipants = Array.from(new Set(
    meetingMinute.meetings.flatMap(meeting =>
      meeting.attendanceList.map(a => a.email).filter(email => email.trim() !== '')
    )
  ));

  const [emailInput, setEmailInput] = useState<string>('');
  const [radioValue, setRadioValue] = useState<string>('allParticipants');
  const [emails, setEmails] = useState<string[]>(allParticipants);
  const [emailError, setEmailError] = useState<string>('');
  const [sendAttachments, setSendAttachments] = useState<boolean>(defaultAttachNoteAttachments);

  const [reportType, setReportType] = useState<string>(operationalDashboardReport ? 'OPERATIONAL_REPORT' : 'MEETING');
  const [customReport, setCustomReport] = useState<boolean>(false);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>(['NEW', 'ACTIVE', 'FINISHED', 'CANCELED']);
  const [selectedPriorities, setSelectedPriorities] = useState<string[]>(['L', 'M', 'H']);
  const [selectedProcess, setSelectedProcess] = useState<string>('');
  const [onlyCurrentMeeting, setOnlyCurrentMeeting] = useState(true);
  const [includeFullItemsHistory, setIncludeFullItemsHistory] = useState(false);

  useEffect(() => {
    onListOfRecipientsChange(emails);
  }, [emails]);

  useEffect(() => {
    setSendAttachments(defaultAttachNoteAttachments);
    onAttachNoteAttachmentsChanged(defaultAttachNoteAttachments);
  }, [defaultAttachNoteAttachments]);

  useEffect(() => {
    const reportDto: ReportRequestParameters = {
      reportType: customReport ? 'CUSTOM' : reportType,
      reportId: null,
      reportSource: 'MeetingMinutes',
      language: 'pl',
      isDynamicReport: customReport,
      selectedStatuses,
      selectedPriorities,
      selectedProcesses: selectedProcess ? [selectedProcess] : [],
      onlyCurrentMeeting,
      includeFullItemsHistory
    };

    onReportRequestParametersChange(reportDto);
  }, [
    reportType,
    customReport,
    selectedStatuses,
    selectedPriorities,
    selectedProcess,
    onlyCurrentMeeting,
    includeFullItemsHistory,
    onReportRequestParametersChange
  ]);

  const handleRadioChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setRadioValue(value);
    if (value === 'meetingParticipants') {
      setEmails(lastMeetingParticipants);
    } else if (value === 'allParticipants') {
      setEmails(allParticipants);
    }
  };

  const handleAddEmail = () => {
    if (emailInput.trim() !== '') {
      if (!validateEmail(emailInput)) {
        setEmailError(l10n.getString("email-incorrect", null, "_email-incorrect"));
      } else if (emails.includes(emailInput)) {
        setEmailError(l10n.getString("email-already-on-list", null, "_email-already-on-list"));
      } else {
        setEmails([...emails, emailInput]);
        setEmailInput('');
        setEmailError('');
      }
    }
  };

  const handleClearEmail = () => {
    setEmailInput('');
    setEmailError('');
  };

  const handleClearEmails = () => {
    setEmails([]);
    setRadioValue('');
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleAddEmail();
    }
  };

  const handleDelete = (emailToDelete: string) => () => {
    setEmails(emails.filter(email => email !== emailToDelete));
  };

  const handleAttachChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSendAttachments(event.target.checked);
    onAttachNoteAttachmentsChanged(event.target.checked);
  };

  const handleEmailChange: AppTextFieldHandler = (e) => {
    setEmailInput(e.target.value);
    if (e.target.value.trim() === '') {
      setEmailError('');
    }
  };

  const toggleSelection = (value: string, list: string[], setList: (v: string[]) => void) => {
    setList(list.includes(value) ? list.filter(v => v !== value) : [...list, value]);
  };

  return (
    <Container>

      {/* Sekcja 1: Opcje raportu */}
      <Paper elevation={3} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom>
          <Localized id="report-how-to-generate">_Jaki raport wygenerować?</Localized>
        </Typography>

        <FormControlLabel
          control={
            <Switch
              checked={customReport}
              onChange={(e) => {
                setCustomReport(e.target.checked);
                if (!e.target.checked) {
                  setSelectedStatuses(selectedStatuses);
                  setSelectedPriorities(selectedPriorities);
                  setSelectedProcess(selectedProcess);
                }
              }}
            />
          }
          label="Tryb zaawansowany (stwórz własny raport)"
        />


        {!customReport && (
          <Box mb={2} mt={2}>
            <SimpleMeetingMinutesReportSelect value={reportType} onChange={setReportType}
              operationalDashboardReport={operationalDashboardReport} />
          </Box>
        )}

        {customReport && (
          <CustomMMReportOptions
            selectedStatuses={selectedStatuses}
            setSelectedStatuses={setSelectedStatuses}
            selectedPriorities={selectedPriorities}
            setSelectedPriorities={setSelectedPriorities}
            selectedProcess={selectedProcess}
            setSelectedProcess={setSelectedProcess}
            processesList={processes}
            onlyCurrentMeeting={onlyCurrentMeeting}
            setOnlyCurrentMeeting={setOnlyCurrentMeeting}
            includeFullItemsHistory={includeFullItemsHistory}
            setIncludeFullItemsHistory={setIncludeFullItemsHistory}
          />
        )}

        <FormControlLabel
          control={<Checkbox checked={sendAttachments} onChange={handleAttachChange} />}
          label={<Localized id="meeting-minutes-add-attachements">_Dodaj do maila załączniki notatki</Localized>}
        />
      </Paper>

      {/* Sekcja 2: Do kogo wysłać */}
      <Paper elevation={3} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} flexWrap="wrap">
          <Typography variant="h6">
            <Localized id="report-send-to-whom">_Do kogo wysłać?</Localized>
          </Typography>
          <Button size="small" disabled={emails.length === 0} onClick={handleClearEmails}>
            <Localized id="meeting-minutes-clear-email-list">_Wyczyść listę maili</Localized>
          </Button>
        </Box>

        <FormControl component="fieldset">
          <RadioGroup value={radioValue} onChange={handleRadioChange}>
            <FormControlLabel
              value="meetingParticipants"
              control={<Radio />}
              label={<Localized id="meeting-minutes-current-meeting-attendees">_Uczestnicy bieżącego spotkania</Localized>}
            />
            <FormControlLabel
              value="allParticipants"
              control={<Radio />}
              label={<Localized id="meeting-minutes-all-attendees">_Wszyscy uczestnicy</Localized>}
            />
          </RadioGroup>
        </FormControl>

        <Paper component="div" variant="outlined" sx={{ p: 1, mt: 2, mb: 2, maxHeight: 300, overflowY: 'auto' }}>
          {emails.sort((a, b) => a.localeCompare(b)).map((email) => (
            <Chip
              key={email}
              label={email}
              onDelete={handleDelete(email)}
              color="primary"
              sx={{ m: 0.5 }}
            />
          ))}
        </Paper>

        <TextField
          value={emailInput}
          size="small"
          onChange={handleEmailChange}
          onKeyDown={handleKeyDown}
          label={<Localized id="add-email">_Dodaj email</Localized>}
          variant="outlined"
          type="email"
          autoComplete="email"
          fullWidth
          error={!!emailError}
          helperText={emailError}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Tooltip title={<Localized id="add-email">_Dodaj email</Localized>}>
                  <IconButton onClick={handleAddEmail} color="success" aria-label="add email">
                    <AddIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title={<Localized id="clear-email">_Wyczyść</Localized>}>
                  <span>
                    <IconButton
                      onClick={handleClearEmail}
                      color="primary"
                      aria-label="clear email"
                      disabled={!emailInput.trim()}
                      sx={{
                        opacity: emailInput ? 1 : 0.5,
                        cursor: emailInput ? 'pointer' : 'not-allowed',
                      }}
                    >
                      <CloseRounded fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </InputAdornment>
            ),
          }}
        />
      </Paper>
    </Container>
  );
};

export default EmailInput;
